<template>
    <div id="summary_section" ref="summary">
        <h2 class="section_heading">Summary</h2>

        <div id="iso_filters">
            <label>Sort By:
                <select v-on:change="filter()" v-model="filters.sort">
                    <option value="Company Name: A to Z">Company Name: A to Z</option>
                    <option value="Company Name: Z to A">Company Name: Z to A</option>

                    <option value="Date Created: Ascending">Date Created: Ascending</option>
                    <option value="Date Created: Descending">Date Created: Descending</option>

                    <option value="Due Date: Ascending">Due Date: Ascending</option>
                    <option value="Due Date: Descending">Due Date: Descending</option>    
                    
                    <option value="Last Updated: Ascending">Last Updated: Ascending</option>
                    <option value="Last Updated: Descending">Last Updated: Descending</option>

                    <option value="Value: Ascending">Value: Ascending</option>
                    <option value="Value: Descending">Value: Descending</option>
                </select>
            </label>

            <label>Company Type
                <select v-model="filters.companyType" v-on:change="filter()">
                    <option value="">Select a Type</option>
                    <option v-bind:value="type" v-for="(type, index) in companyTypes" v-bind:key="index">{{ type }}</option>
                </select>
            </label>

            <label>Client Type
                <select v-model="filters.clientType" v-on:change="filter()">
                    <option value="">Select a Type</option>
                    <option v-bind:value="type" v-for="(type, index) in clientTypes" v-bind:key="index">{{ type }}</option>
                </select>
            </label>

            <label>Business Lines
                <select v-model="filters.businessLine" v-on:change="filter()">
                    <option value="">Select a Business Line</option>
                    <option v-bind:value="line" v-for="(line, index) in businessLines" v-bind:key="index">{{ line }}</option>
                </select>
            </label>

            <label>Contract Types
                <select v-model="filters.contractType" v-on:change="filter()">
                    <option value="">Select a Type</option>
                    <option v-bind:value="type" v-for="(type, index) in contractTypes" v-bind:key="index">{{ type }}</option>
                </select>
            </label>

            <label>Search
                <input type="text" v-model.trim="filters.search" v-on:input="filter()">
            </label>

            
            <label v-if="user.user_role == 'Manager'">ISO Ownership
                <select v-model="filters.userIsoOwnershipManager" v-on:change="filter()">
                    <option value="">All</option>
                    <option v-for="(companyUser, index) in getCompanyUsers" v-bind:value="companyUser._id" v-bind:key="index">{{ companyUser.name }}</option>
                </select>
            </label>

            <label v-else>ISO Ownership
                <input type="checkbox" v-model="filters.userIsoOwnership" v-on:change="filter()">
            </label>
        </div>

        <div id="board">
            <div class="column" v-for="(column, index) in boardColumns" v-bind:key="index">
                <div class="head">
                    <!-- <p class="title">{{ column.name }} <span>#{{ column.post_count }}</span></p> -->
                    <p class="title">{{ column.name }} <span>#{{ boardPosts[index].length }}</span></p>
                    <p class="percentage">{{ column.percentage.$numberDecimal * 100 }}%</p>
                    <hr>
                    <p class="raw_total">{{ formatMoneyValue(column.total.$numberDecimal) }}</p>
                    <p class="calculated_total">{{ formatMoneyValue(column.total.$numberDecimal * column.percentage.$numberDecimal) }}</p>
                </div>

                <div class="direct-iso-addition" v-on:click="openIsoForm(index)">
                    <svg viewBox="0 0 32 32">
                        <path d="M28,14H18V4c0-1.104-0.896-2-2-2s-2,0.896-2,2v10H4c-1.104,0-2,0.896-2,2s0.896,2,2,2h10v10c0,1.104,0.896,2,2,2  s2-0.896,2-2V18h10c1.104,0,2-0.896,2-2S29.104,14,28,14z"/>
                    </svg>
                </div>

                <div class="body">
                    <draggable 
                        v-model="boardPosts[index]" 
                        group="board" 
                        v-on:end="dragEnd"
                        v-bind:data-column-index="index"
                        class="drop_zone"
                    >
                        <BoardCard
                            v-for="(post, isoIndex) in boardPosts[index]" 
                            v-bind:key="post._id"
                            v-bind:company="post.company_name"
                            v-bind:closeBy="post.close_by_date"
                            v-bind:business_line="post.lines_of_business"
                            v-bind:value="formatMoneyValue(post.annual_value)"
                            v-on:click.native="toggleUpdateForm(post, isoIndex)"
                        />
                    </draggable>
                </div>
            </div>
        </div>

        <button id="scroll-button" v-if="scrollButton" v-on:click="scroll_to_top">
            <svg viewBox="0 0 22 12">
                <g transform="matrix(1,0,0,1,-60.16,-19.94)">
                    <g transform="matrix(6.12323e-17,1,-1,6.12323e-17,92.49,10)">
                        <g transform="matrix(-1,-1.22465e-16,1.22465e-16,-1,42.78,43.44)">
                            <g id="Layer_1-2">
                                <path d="M21.39,11.11L32.84,21.74L21.39,32.44L21.39,24.64L24.54,21.74L21.39,19.21L21.39,11.11Z" style="fill:white;fill-rule:nonzero;"/>
                            </g>
                        </g>
                    </g>
                </g>
            </svg>
        </button>

        <div id="iso-date-update" v-if="isoDateForm">
            <p>Iso Due Date is required to move. Please update.</p>

            <label>
                Due Date
                <input type="date" name="due_date" v-model="isoDueDate">
            </label>

            <button v-on:click="cancelIsoUpdate">Cancel</button>
            <button v-on:click="update_iso_date">Update</button>
        </div>

        <div v-if="permissionError" id="permission-form">
            <p>Permission denied. You cannot update other users ISO\'s</p>

            <button v-on:click="permissionError = false">Ok</button>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BoardCard from './BoardCard.vue';
import draggable from 'vuedraggable';
import axios from 'axios';
import $ from 'jquery';

export default {
    components: {
        BoardCard,
        draggable
    },
    data() {
        return {
            scrollButton: false,
            isoDateForm: false,
            isoDueDate: '',
            isoIndex: {},
            permissionError: false
        }
    },
    mounted() {
        window.addEventListener('scroll', this.handleScroll);
    },
    created() {
    },
    computed: {
        ...mapGetters([
            'boardPosts',
            'boardColumns',
            'businessLines',
            'clientTypes',
            'contractTypes',
            'companyTypes',
            'filters',
            'getCompanyUsers',
            'user'
        ])
    },
    methods: {
        ...mapActions([
            'toggleSubmitFormVisibility',
            'filter_isos',
        ]),
        toggleUpdateForm(postData, isoIndex) {
            // console.log(postData);
            // add the iso index to the postData object so that if it is moved to another column during the update we can use the index to splice it from the array and add it in its new position
            postData['isoIndex'] = isoIndex;
            this.$emit('toggle', postData);
        },
        dragEnd(e) {
            let newColumn = e.to.dataset.columnIndex;
            let newIndex = e.newIndex;
            let oldColumn = e.from.dataset.columnIndex;
            let oldIndex = e.oldIndex;

            // save iso indexs to use later when we update the isos posisiton or date
            this.isoIndex = {
                oldColumn,
                oldIndex,
                newColumn,
                newIndex
            };

            // if iso has no date and we aren't moving the iso to the first column which doesn't require a date
            if (this.boardPosts[newColumn][newIndex].due_date === '' && newColumn !== '0') {
                this.isoDateForm = true;
            } else {
                // date is set, move iso to new position
                this.move_iso(newColumn, newIndex);
            }
        },
        cancelIsoUpdate() {
            // if the user decides to not update the iso date we move it back

            // remove post from new position in array
            let post = this.boardPosts[this.isoIndex.newColumn].splice(this.isoIndex.newIndex, 1);
            // put post object back into old position in array
            this.boardPosts[this.isoIndex.oldColumn].splice(this.isoIndex.oldIndex, 0, post[0]);

            // reset iso data and close form
            this.isoDateForm = false;
            this.isoIndex = {};
            this.isoDueDate = '';
        },
        update_iso_date() {
            // the user has chosen to update the date

            // check to see if user has selected a date
            if (this.isoDueDate !== '') {
                // move the iso and pass new date
                this.move_iso(this.isoIndex.newColumn, this.isoIndex.newIndex, this.isoDueDate);

                // close form
                this.isoDateForm = false;
            }
        },
        move_iso(column, index, date) {
            let postId = this.boardPosts[column][index]._id;
            let authorId = this.boardPosts[column][index].author_id;

            let post = {
                id: postId,
                newColumn: column,
                author_id: authorId
            };

            // if date is set include date in post object to be updated on server side
            if (date !== undefined) {
                post['dueDate'] = date;
            }

            axios.post('api/summary-board/move', post)
                .then(() => {
                    // console.log('get response from route here to add dispatch below')
                    this.$store.dispatch('getAllBoardPostData');

                    // clear iso indexes
                    this.isoIndex = {};
                    this.isoDueDate = '';
                })
                .catch((error) => {
                    console.log(error);

                    if (error.response.status === 403) {
                        this.permissionError = true;
                        this.cancelIsoUpdate();


                        // this.validation.msg = 'Permission denied. You cannot edit other users ISO\'s';
                        // this.validation.error = true;
                    } else {
                        // this.validation.msg = '';
                        // this.validation.error = false;
                    }
                });
        },
        formatMoneyValue(value) {
            return new Intl.NumberFormat('en-GB', { 
                style: 'currency', 
                currency: 'GBP',
                maximumFractionDigits: value > 99.9 ? 0 : 2,
                minimumFractionDigits: value > 99.9 ? 0 : 2,
            }).format(value);
        },
        handleScroll() {
            let windowPos = window.scrollY;

            if (windowPos > this.$refs.summary.offsetTop) {
                this.scrollButton = true;
            }

            if (windowPos <= this.$refs.summary.offsetTop) {
                this.scrollButton = false;
            }
        },
        scroll_to_top() {
            $('html, body').animate({
                scrollTop: this.$refs.summary.offsetTop
            }, 1000);
        },
        openIsoForm(columnIndex) {
            // pass column index for iso to store action
            this.toggleSubmitFormVisibility(columnIndex);
        },
        filter() {
            this.$store.commit('update_filter_values', this.filters);
            this.filter_isos();
        }
    }
}
</script>

<style lang="scss" scoped>
    .section_heading{
        font-size: 3rem;
        font-weight: 700;
        line-height: 1.3;
        color: $primary-three;
    }

    #summary_section{
        padding: 5rem 0 5rem 5rem;
        // background-color: #eaeaea;
        border-top: 0.1rem solid $primary-two;
        border-bottom: 0.1rem solid $primary-two;
        overflow-x: scroll;
        min-height: 100vh;

        #iso_filters {
            display: flex;
            margin-top: 2rem;

            label{
                font-size: 1.8rem;
                line-height: 1.3;
                font-weight: 600;
                color: #6693bc;
                margin-right: 1.5rem;

                input{
                    display: block;
                    // width: 100%;
                    margin-top: 0.5rem;
                    border: 0.1rem solid #355364;
                    font-size: 1.6rem;
                    line-height: 1.3;
                    font-weight: 500;
                    color: #355364;
                    padding: 0.25rem 0.5rem;
                }

                select {
                    display: block;
                    // width: 100%;
                    margin-top: 0.5rem;
                    border: 0.1rem solid #355364;
                    font-size: 1.6rem;
                    line-height: 1.3;
                    font-weight: 500;
                    color: #355364;
                    padding: 0.25rem 0.5rem;
                }

            }
        }

        #iso-date-update, #permission-form {
            display: flex;
            flex-wrap: wrap;
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: #fff;
            border: 0.3rem solid #6693bc;
            padding: 3rem;
            width: 42rem;

            p {
                font-size: 2.2rem;
                margin: 0 0 2rem 0;
                width: 100%;
                line-height: 1.3;
                font-weight: 600;
                color: $primary-two;
            }

            label {
                width: 100%;
                font-size: 1.8rem;
                line-height: 1.3;
                font-weight: 600;
                margin-bottom: 3rem;
                color: $primary-two;

                input{
                    display: block;
                    width: 100%;
                    margin-top: 0.5rem;
                    border: 0.1rem solid $primary-one;
                    font-size: 1.6rem;
                    line-height: 1.3;
                    font-weight: 500;
                    color: $primary-one;
                    padding: 0.25rem 0.5rem;
                }
            }

            button {
                font-size: 1.6rem;
                line-height: 1.3;
                font-weight: 600;
                color: #fff;
                background-color: $primary-two;
                border: 0;
                padding: 1rem;
                width: 33%;
                margin: 0 auto;
            }
        }

        #board{
            display: flex;
            margin-top: 4rem;

            .column{
                display: flex;
                flex-direction: column;
                width: 30rem;
                min-width: 28rem;
                margin-right: 2rem;

                &:nth-of-type(1){
                    .head{
                        border-top: 0.5rem solid #83dd1a;
                    }
                }

                &:nth-of-type(2){
                    .head{
                        border-top: 0.5rem solid #d5d814;
                    }
                }

                &:nth-of-type(3){
                    .head{
                        border-top: 0.5rem solid #fdab2f;
                    }
                }

                &:nth-of-type(4){
                    .head{
                        border-top: 0.5rem solid #1ad4dd;
                    }
                }

                &:nth-of-type(5){
                    box-sizing: unset;
                    padding-left: 2rem;
                    border-left: 0.5rem solid $primary-one;

                    .head{
                        border-top: 0.5rem solid #0063e8;
                    }
                }

                &:nth-of-type(6){
                    .head{
                        border-top: 0.5rem solid #7c0aa5;
                    }
                }

                &:nth-of-type(7){
                    .head{
                        border-top: 0.5rem solid #dd1aa1;
                    }
                }

                .head{
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: space-between;
                    background-color: $primary-two;
                    padding: 1rem 1.5rem;
                    border-radius: 0.5rem;
                    // margin-bottom: 2rem;

                    .title{
                        font-size: 2rem;
                        font-weight: 700;
                        line-height: 1.3;
                        color: #fff;

                        span{
                            font-weight: 300;
                        }
                    }

                    .percentage{
                        font-size: 2rem;
                        line-height: 1.3;
                        font-weight: 500;
                        color: #fff;
                        color: rgba(255, 255, 255, 0.4);
                    }
                    
                    hr{
                        width: 100%;
                        border: unset;
                        border-top: 0.2rem solid $primary-one;
                    }

                    .raw_total{
                        font-size: 1.8rem;
                        line-height: 1.3;
                        font-weight: 500;
                        color: #fff;
                    }

                    .calculated_total{
                        font-size: 1.8rem;
                        line-height: 1.3;
                        font-weight: 500;
                        color: #fff;
                        color: rgba(255, 255, 255, 0.4);
                    }
                }

                .direct-iso-addition {
                    display: flex;
                    justify-content: center;
                    padding: 0.5rem 0;
                    border: 0.2rem solid $primary-two;
                    border-radius: 0.5rem;
                    margin-top: 1rem;
                    background-color: #fff;

                    svg {
                        width: 2rem;
                        height: 2rem;

                        path {
                            fill: $primary-one;
                        }
                    }
                }

                .body{
                    flex-grow: 1;

                    .drop_zone{
                        min-height: 10rem;
                        height: 100%;
                    }
                }
            }
        }

        #scroll-button {
            position: fixed;
            bottom: 0;
            left: 35rem;
            background-color: #355364;
            padding: 1.2rem 1rem;
            border: 0;
            outline: 0;

            svg {
                width: 3rem;
                height: auto;
            }
        }
    }
</style>